import React, { useEffect, useState } from 'react';
import { Loader2, Server, Tag, Star, Users, Radio } from 'lucide-react';
import api from '@/lib/adminApi';

const PlanCard = ({ p }) => (
  <div className="rounded-2xl border border-white/10 bg-surface/70 p-5">
    <div className="flex items-start justify-between">
      <div>
        <p className="flex items-center gap-1.5 font-display text-base font-bold text-white">
          {p.name} {p.is_featured && <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />}
        </p>
        <span className="mt-1 inline-block rounded-full bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white/50">{p.user_type}</span>
      </div>
      <div className="text-right">
        <p className="font-display text-2xl font-bold text-brand">{p.currency === 'USD' ? '$' : ''}{p.price_monthly ?? p.price}</p>
        <p className="text-[10px] text-white/40">/mo{p.price_yearly ? ` · $${p.price_yearly}/yr` : ''}</p>
      </div>
    </div>
    <p className="mt-3 whitespace-pre-line text-xs leading-relaxed text-white/50">{p.description}</p>
    <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
      {p.max_trades_per_month != null && <span className="rounded bg-white/5 px-2 py-1 text-white/60">{p.max_trades_per_month} trades/mo</span>}
      {p.max_subscriptions != null && <span className="rounded bg-white/5 px-2 py-1 text-white/60">{p.max_subscriptions} providers</span>}
      {p.max_telegram_channels != null && <span className="rounded bg-white/5 px-2 py-1 text-white/60">{p.max_telegram_channels} channels</span>}
      {p.max_followers != null && <span className="rounded bg-white/5 px-2 py-1 text-white/60">{p.max_followers} followers</span>}
    </div>
    {(p.stripe_price_id || p.apple_product_id) && (
      <div className="mt-3 space-y-0.5 border-t border-white/10 pt-2 text-[10px] text-white/30">
        {p.stripe_price_id && <p className="truncate">Stripe: {p.stripe_price_id}</p>}
        {p.apple_product_id && <p className="truncate">Apple: {p.apple_product_id}</p>}
      </div>
    )}
  </div>
);

export default function AdminPlans() {
  const [plans, setPlans] = useState(null);
  const [servers, setServers] = useState([]);

  useEffect(() => {
    api.get('/admin/plans').then((r) => setPlans(r.data.plans || []));
    api.get('/admin/mt5-servers').then((r) => setServers(r.data.servers || []));
  }, []);

  if (!plans) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const followers = plans.filter((p) => p.user_type === 'follower');
  const providers = plans.filter((p) => p.user_type === 'provider');

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Plans &amp; Infrastructure</h1>
        <p className="mt-1 text-sm text-white/50">Live from your production API (api.prod.copy.click) — real subscription plans and MT5 servers.</p>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-white"><Users className="h-4 w-4 text-brand" /> Follower Plans</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {followers.map((p) => <PlanCard key={p.id} p={p} />)}
        </div>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-white"><Radio className="h-4 w-4 text-[#00FFA3]" /> Provider Plans</h2>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {providers.map((p) => <PlanCard key={p.id} p={p} />)}
        </div>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-white"><Server className="h-4 w-4 text-brand" /> Supported MT5 Servers ({servers.length})</h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {servers.map((s) => (
            <div key={s.id} data-testid="admin-mt5-server-row" className="flex items-center gap-3 rounded-xl border border-white/10 bg-surface/70 px-4 py-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand"><Server className="h-4 w-4" /></span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-white">{s.name}</p>
                {s.installer_url && <a href={s.installer_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-white/40 hover:text-brand">Installer ↗</a>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
